import { ArrowLeft } from 'lucide-react'
import { navigate } from '../../lib/router'
import { useAuthStore } from '../../lib/auth-store'
import { PublicHeader } from '../../components/marketing/PublicHeader'
import { Footer } from '../../components/Footer'
import { Button } from '../../components/ui/button'

const h2 = 'text-2xl font-semibold tracking-tight'
const list = 'list-disc space-y-1 pl-5'

export function PrivacyPage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground">
      <PublicHeader loggedIn={!!accessToken} />
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 pt-32 pb-24">
        <article className="space-y-6 text-sm leading-relaxed text-foreground/90 md:text-base">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="-ml-2 text-muted-foreground">
            <ArrowLeft /> Back
          </Button>

          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Privacy Policy</h1>
            <p className="text-xs text-muted-foreground">Last updated: April 2026</p>
          </header>

          <section className="space-y-2">
            <h2 className={h2}>1. Controller</h2>
            <p>
              Applox GmbH, [ADDRESS], Germany<br />
              Email: privacy@doable.website
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>2. What data we collect</h2>
            <ul className={list}>
              <li><strong>Account data:</strong> email address, name, hashed password (argon2id). Collected at signup.</li>
              <li><strong>Project data:</strong> project names, prompts, generated source code, build logs. Created when you use the platform.</li>
              <li><strong>Workspace settings:</strong> your Anthropic API key, encrypted at rest (AES-256-GCM). We cannot read it in plaintext.</li>
              <li><strong>Audit log:</strong> actions performed (login, project create/delete/build), IP addresses, timestamps.</li>
              <li><strong>Technical data:</strong> server access logs, error reports, request metadata.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>3. How we use your data</h2>
            <ul className={list}>
              <li>To provide and operate the Doable platform (Art. 6(1)(b) GDPR — contract performance).</li>
              <li>To authenticate you and secure your account.</li>
              <li>To send your prompts to the Anthropic API using <strong>your own API key</strong> so Claude can generate code.</li>
              <li>To deploy generated applications to our infrastructure on your behalf.</li>
              <li>To maintain audit logs for security and accountability.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>4. Where data is processed</h2>
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="h-10 px-2 text-left text-xs font-medium text-muted-foreground">System</th>
                    <th className="h-10 px-2 text-left text-xs font-medium text-muted-foreground">Location</th>
                    <th className="h-10 px-2 text-left text-xs font-medium text-muted-foreground">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2">Hetzner Cloud</td><td className="p-2">Falkenstein, Germany (EU)</td><td className="p-2">Platform infrastructure, code storage, deployment</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Error monitoring (GlitchTip)</td><td className="p-2">Falkenstein, Germany (EU)</td>
                    <td className="p-2">
                      Self-hosted by Applox GmbH on servers of Hetzner Online GmbH; error reports without IP addresses,
                      cookies or user identifiers; no third party involved.
                    </td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Brevo</td><td className="p-2">EU</td><td className="p-2">Transactional email (verification, password reset)</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Anthropic API</td><td className="p-2">USA</td>
                    <td className="p-2">
                      AI code generation — accessed exclusively via <strong>your own API key</strong> and your own Anthropic account.
                      Your Anthropic Data Processing Agreement governs this processing.
                      Doable does not act as a controller or processor for data sent to Anthropic via your key.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>5. Data retention</h2>
            <ul className={list}>
              <li><strong>Account data:</strong> retained until you delete your account, plus a 30-day grace period.</li>
              <li><strong>Project data:</strong> retained until you delete the project. Fully removed within 30 days of deletion.</li>
              <li><strong>Audit logs:</strong> retained for 12 months, then purged.</li>
              <li><strong>Server logs:</strong> retained for 90 days.</li>
              <li><strong>Error reports:</strong> retained for 90 days.</li>
              <li><strong>API key:</strong> deleted immediately when you remove it from workspace settings.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>6. Your rights (GDPR Art. 12–23)</h2>
            <p>You have the right to:</p>
            <ul className={list}>
              <li>Access, rectify, or erase your personal data.</li>
              <li>Restrict or object to processing.</li>
              <li>Data portability (receive your data in a structured format).</li>
              <li>Withdraw consent at any time (where processing is based on consent).</li>
              <li>Lodge a complaint with your supervisory authority.</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact <strong>privacy@doable.website</strong>.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>7. Cookies and tracking</h2>
            <p>
              Doable uses only essential cookies (authentication session). We do not use
              analytics, tracking pixels, or third-party advertising cookies. No consent
              banner is required because we use no non-essential cookies.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>8. Security measures</h2>
            <ul className={list}>
              <li>Passwords hashed with argon2id (memory-hard, timing-safe).</li>
              <li>API keys encrypted at rest with AES-256-GCM.</li>
              <li>All inter-server traffic encrypted via WireGuard.</li>
              <li>HTTPS with TLS 1.2+ on all public endpoints.</li>
              <li>Per-project container isolation (capability-dropped, read-only, memory-limited).</li>
              <li>Hetzner Cloud Firewalls with outbound whitelist.</li>
              <li>Automated security updates (unattended-upgrades).</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className={h2}>9. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The "last updated" date at the top
              reflects the latest revision. Continued use after changes constitutes acceptance.
            </p>
          </section>
        </article>
      </main>
      <Footer variant="site" />
    </div>
  )
}
